<div class="student-layout">
  <!-- ── Header: title + state badge ───────────────────────── -->
  <div class="student-header">
    <h2 class="student-title">{{ task.title }}</h2>
    <app-state-box [state]="data.registration.state?.name" [student]="true" position="left"></app-state-box>
  </div>

  @if (task.description) {
    <p class="description-text">{{ task.description }}</p>
  }

  <!-- ── Body ───────────────────────────────────────────────── -->

  <!-- New submission form -->
  @if (!data.registration.registrations || data.registration.registrations.length < 1 || resubmit) {
    @if (data.registration.task.toSubmit && data.registration.state && data.registration.state.name !== 'FINISHED') {
      <div class="content-card">
        <p class="section-header">{{ 'case.tasks.choose-registrations-to-submit' | translate | capitalize }}</p>

        @if (!loadingFunctions && task.functionToSubmit !== null && task.functionToSubmit.id !== null) {
          <p class="hint-text">
            <i>{{
              'case.tasks.function-registration-needs-submitted'
                | translate: { FUNCTION_NAME: task.functionToSubmit.title }
                | capitalize
            }}</i>
          </p>
        }

        <!-- Already-added items -->
        @if (
          newTaskWorkgroupRegistration &&
          newTaskWorkgroupRegistration.functionRegistrations &&
          newTaskWorkgroupRegistration.functionRegistrations.length > 0
        ) {
          @for (newReg of newTaskWorkgroupRegistration.functionRegistrations; track newReg) {
            <div class="added-item">
              <mat-icon class="added-item-icon">insert_drive_file</mat-icon>
              <div class="added-item-info">
                <span class="added-item-title">{{ newReg.function.title }}</span>
                <span class="added-item-date">
                  {{ 'general.words.on' | translate }}&nbsp;
                  @if (newReg.data.finalSince > 1) {
                    <b>{{ newReg.data.finalSince | date: 'dd-MM-yyyy' }}</b
                    >&nbsp;{{ 'general.words.at' | translate }}&nbsp;<b>{{ newReg.data.finalSince | date: 'HH:mm' }}</b>
                  }
                  @if (newReg.data.finalSince < 1 && newReg.data.registered > 1) {
                    <b>{{ newReg.data.registered | date: 'dd-MM-yyyy' }}</b
                    >&nbsp;{{ 'general.words.at' | translate }}&nbsp;<b>{{ newReg.data.registered | date: 'HH:mm' }}</b>
                  }
                </span>
              </div>
              <span class="fill"></span>
              <button
                mat-icon-button
                class="delete-btn"
                (click)="deleteNewFunctionRegistrationFromList(newReg)"
                aria-label="Remove">
                <mat-icon>delete</mat-icon>
              </button>
              <button mat-icon-button color="primary" (click)="openNewFunctionRegistration(newReg)" aria-label="View">
                <mat-icon>visibility</mat-icon>
              </button>
            </div>
          }
        }

        <!-- Function picker -->
        <form [formGroup]="newRegistrationFormGroup" class="picker-form">
          @if (loadingFunctions) {
            <mat-spinner diameter="36"></mat-spinner>
          }
          @if (!loadingFunctions) {
            <mat-form-field class="full-width" appearance="outline" [subscriptSizing]="'dynamic'">
              <mat-label>{{ 'general.words.EPD-function' | translate | capitalize }}</mat-label>
              <mat-select
                formControlName="function"
                (selectionChange)="onNewSubmissionFunctionSelectChange($event)"
                [disabled]="task.functionToSubmit !== null">
                @for (category of availableFunctions; track category) {
                  <mat-optgroup>
                    {{ category.name }}
                    @for (function of category.categoryAppFunctions; track function) {
                      <mat-option [value]="function.appFunction">{{ function.appFunction.title }}</mat-option>
                    }
                  </mat-optgroup>
                }
              </mat-select>
            </mat-form-field>
          }

          <!-- formGroup sub-selects -->
          @if (selectedFunction === 'formGroup') {
            @if (formGroupFunctions && formGroupFunctions.length > 0 && !loadingFormGroup) {
              <mat-form-field class="full-width" appearance="outline" [subscriptSizing]="'dynamic'">
                <mat-label>{{ 'general.words.form' | translate | capitalize }}</mat-label>
                <mat-select formControlName="formGroupForm" (selectionChange)="onFormGroupChange($event)">
                  @for (function of formGroupFunctions | sortBy: 'asc' : 'order'; track function) {
                    <mat-option [value]="function">{{ function.title }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
            @if (functionRegistrations && functionRegistrations.length > 0) {
              <div class="reg-row">
                <mat-form-field appearance="outline" class="reg-field" [subscriptSizing]="'dynamic'">
                  <mat-label>{{ 'general.words.registrations' | translate | capitalize }}</mat-label>
                  <mat-select
                    formControlName="formGroupFormRegistration"
                    (selectionChange)="onformGroupFunctionRegistrationChange($event)">
                    @for (reg of functionRegistrations; track reg) {
                      @if (reg.registered && reg.registered > 1 && reg.state === 'FINAL') {
                        <mat-option [value]="reg">
                          {{ 'general.words.on' | translate | capitalize }}&nbsp;<b>{{
                            reg.registered | date: 'dd-MM-yyyy'
                          }}</b
                          >&nbsp;{{ 'general.words.at' | translate | capitalize }}&nbsp;<b>{{
                            reg.registered | date: 'HH:mm'
                          }}</b>
                        </mat-option>
                      }
                    }
                  </mat-select>
                </mat-form-field>
                @if (dataRegistration) {
                  <button mat-icon-button color="primary" (click)="openDataRegistration('form')" class="view-btn">
                    <mat-icon>visibility</mat-icon>
                  </button>
                }
              </div>
            }
          }

          <!-- anamnesis sub-selects -->
          @if (selectedFunction === 'anamnesis') {
            @if (
              anamnesisFunctionSetFunctions && anamnesisFunctionSetFunctions.length > 0 && !loadingAnamnesisFunctionSets
            ) {
              <mat-form-field class="full-width" appearance="outline" [subscriptSizing]="'dynamic'">
                <mat-label>{{ 'general.words.form' | translate | capitalize }}</mat-label>
                <mat-select formControlName="anamnesisForm" (selectionChange)="onAnanmesisFunctionChange($event)">
                  @for (function of anamnesisFunctionSetFunctions | sortBy: 'asc' : 'order'; track function) {
                    <mat-option [value]="function">{{ function.title }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
            @if (functionRegistrations && functionRegistrations.length > 0) {
              <div class="reg-row">
                <mat-form-field appearance="outline" class="reg-field" [subscriptSizing]="'dynamic'">
                  <mat-label>{{ 'general.words.registrations' | translate | capitalize }}</mat-label>
                  <mat-select
                    formControlName="anamnesisFormRegistration"
                    (selectionChange)="onAnanmesisFunctionRegistrationChange($event)">
                    @for (reg of functionRegistrations; track reg) {
                      @if (reg.registered && reg.registered > 1 && reg.state === 'FINAL') {
                        <mat-option [value]="reg">
                          {{ 'general.words.on' | translate | capitalize }}&nbsp;<b>{{
                            reg.registered | date: 'dd-MM-yyyy'
                          }}</b
                          >&nbsp;{{ 'general.words.at' | translate | capitalize }}&nbsp;<b>{{
                            reg.registered | date: 'HH:mm'
                          }}</b>
                        </mat-option>
                      }
                    }
                  </mat-select>
                </mat-form-field>
                @if (dataRegistration) {
                  <button mat-icon-button color="primary" (click)="openDataRegistration('form')" class="view-btn">
                    <mat-icon>visibility</mat-icon>
                  </button>
                }
              </div>
            }
          }

          <!-- careplan sub-selects -->
          @if (selectedFunction === 'careplan') {
            @if (loadingCareplanRegistrations) {
              <mat-spinner diameter="36"></mat-spinner>
            }
            @if (!loadingCareplanRegistrations) {
              <div class="reg-row">
                <mat-form-field class="full-width reg-field" appearance="outline" [subscriptSizing]="'dynamic'">
                  <mat-label>{{ 'general.words.registrations' | translate | capitalize }}</mat-label>
                  <mat-select
                    formControlName="careplanRegistration"
                    (selectionChange)="onCareplanRegistrationChange($event)">
                    @for (reg of functionRegistrations; track reg) {
                      <mat-option [value]="reg">
                        {{ 'general.words.on' | translate | capitalize }}&nbsp;<b>{{
                          reg.finalSince | date: 'dd-MM-yyyy'
                        }}</b
                        >&nbsp;{{ 'general.words.at' | translate | capitalize }}&nbsp;<b>{{
                          reg.finalSince | date: 'HH:mm'
                        }}</b>
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                @if (dataRegistration) {
                  <button mat-icon-button color="primary" (click)="openDataRegistration('careplan')" class="view-btn">
                    <mat-icon>visibility</mat-icon>
                  </button>
                }
              </div>
            }
          }

          <!-- careplan-nnn sub-selects -->
          @if (selectedFunction === 'careplan-nnn') {
            @if (loadingCareplanRegistrations) {
              <mat-spinner diameter="36"></mat-spinner>
            }
            @if (!loadingCareplanRegistrations) {
              <div class="reg-row">
                <mat-form-field class="full-width reg-field" appearance="outline" [subscriptSizing]="'dynamic'">
                  <mat-label>{{ 'general.words.registrations' | translate | capitalize }}</mat-label>
                  <mat-select
                    formControlName="careplanRegistration"
                    (selectionChange)="onCareplanNnnRegistrationChange($event)">
                    @for (reg of functionRegistrations; track reg) {
                      <mat-option [value]="reg">
                        {{ 'general.words.on' | translate | capitalize }}&nbsp;<b>{{
                          reg.finalSince | date: 'dd-MM-yyyy'
                        }}</b
                        >&nbsp;{{ 'general.words.at' | translate | capitalize }}&nbsp;<b>{{
                          reg.finalSince | date: 'HH:mm'
                        }}</b>
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                @if (dataRegistration) {
                  <button
                    mat-icon-button
                    color="primary"
                    (click)="openDataRegistration('careplan-nnn')"
                    class="view-btn">
                    <mat-icon>visibility</mat-icon>
                  </button>
                }
              </div>
            }
          }

          <!-- generic form sub-select -->
          @if (loadingFunctionRegistrations || loadingAnamnesisFunctionSets) {
            <mat-spinner diameter="36"></mat-spinner>
          }
          @if (
            !loadingFunctionRegistrations &&
            selectedFunction &&
            selectedFunction !== '' &&
            selectedFunction !== 'anamnesis' &&
            selectedFunction !== 'careplan' &&
            selectedFunction !== 'careplan-nnn' &&
            selectedFunction !== 'formGroup'
          ) {
            <mat-form-field class="full-width" appearance="outline" [subscriptSizing]="'dynamic'">
              <mat-label>{{ 'general.words.registrations' | translate | capitalize }}</mat-label>
              <mat-select formControlName="formRegistration" (selectionChange)="onFormRegistrationChange($event)">
                @for (reg of functionRegistrations; track reg) {
                  @if (reg.finalSince && reg.finalSince > 1 && reg.state === 'FINAL') {
                    <mat-option [value]="reg">
                      {{ 'general.words.on' | translate | capitalize }}&nbsp;<b>{{
                        reg.finalSince | date: 'dd-MM-yyyy'
                      }}</b
                      >&nbsp;{{ 'general.words.at' | translate | capitalize }}&nbsp;<b>{{
                        reg.finalSince | date: 'HH:mm'
                      }}</b>
                    </mat-option>
                  }
                  @if (reg.finalSince < 1 && reg.registered && reg.registered > 1 && reg.state === 'FINAL') {
                    <mat-option [value]="reg">
                      {{ 'general.words.on' | translate | capitalize }}&nbsp;<b>{{
                        reg.registered | date: 'dd-MM-yyyy'
                      }}</b
                      >&nbsp;{{ 'general.words.at' | translate | capitalize }}&nbsp;<b>{{
                        reg.registered | date: 'HH:mm'
                      }}</b>
                    </mat-option>
                  }
                }
              </mat-select>
            </mat-form-field>
          }
        </form>

        <div class="picker-add-row">
          <button
            mat-raised-button
            color="primary"
            [disabled]="!dataRegistration"
            (click)="addNewFunctionRegistration()">
            {{ 'general.actions.add' | translate | capitalize }}
          </button>
        </div>
      </div>
    }

    <!-- Nothing to submit -->
    @if (
      !data.registration.task.toSubmit && data.registration.state.name !== 'FINISHED' && !data.registration.task.isQuiz
    ) {
      <p class="hint-text">{{ 'case.tasks.nothing-needs-submitted' | translate | capitalize }}.</p>
    }

    <!-- Comments -->
    @if (
      data.registration.task.toSubmit &&
      data.registration.state &&
      data.registration.state.name !== 'FINISHED' &&
      !data.registration.task.isQuiz
    ) {
      <div class="content-card">
        <p class="section-header">{{ 'general.words.comments' | translate | capitalize }}</p>
        <mat-form-field appearance="outline" class="full-width" [subscriptSizing]="'dynamic'">
          <mat-label>{{ 'general.words.comments' | translate | capitalize }}</mat-label>
          <textarea
            matInput
            [formControl]="commentsFormControl"
            (keyup)="checkSubmitButtonState()"
            class="comments-textarea"></textarea>
        </mat-form-field>
      </div>
    }

    <!-- Quiz result -->
    @if (data.registration.task.isQuiz) {
      @if (!quizResult) {
        <p class="hint-text">{{ 'case.quiz.submit-quiz-on-case-page' | translate | capitalize }}</p>
      }
      @if (quizResult) {
        <div class="content-card">
          <h3 class="section-header">{{ 'case.quiz-results-title' | translate | capitalize }}</h3>
          <p class="quiz-score">
            <span
              >{{ 'case.quiz.score' | translate | capitalize }}:&nbsp;{{ quizResult.score
              }}{{ 'case.quiz.max-score' | translate | capitalize }}&nbsp;{{ quizResult.maxScore }}</span
            >
            @if (checkIfQuizResultPassed()) {
              <mat-icon class="pass-icon">check_circle</mat-icon>
            } @else {
              <mat-icon class="fail-icon">cancel</mat-icon>
            }
          </p>
          <p class="quiz-meta">
            {{
              'case.quiz.registered'
                | translate
                  : { DATE: quizResult.registered | date: 'dd-MM-yyyy', TIME: quizResult.registered | date: 'HH:mm' }
                | capitalize
            }}
          </p>
          <p class="quiz-meta">
            {{ 'case.quiz.used-time' | translate | capitalize }}: {{ quizResult.usedTime }}
            {{ 'general.words.seconds' | translate }}
          </p>
        </div>
      }
    }
  }

  <!-- Past submissions -->
  @if (data.registration.registrations && data.registration.registrations.length > 0 && !resubmit) {
    <app-task-submission-panel
      [registration]="data.registration.registrations[0]"
      [task]="task"
      [expanded]="true"
      (viewRegistration)="openFunctionRegistrationData($event)">
    </app-task-submission-panel>

    @if (data.registration.registrations.length > 1) {
      <p class="previous-label">{{ 'case.tasks.previously-submitted' | translate | capitalize }}:</p>
    }
    @for (reg of data.registration.registrations; track reg; let i = $index) {
      @if (i !== 0) {
        <mat-accordion>
          <app-task-submission-panel
            [registration]="reg"
            [task]="task"
            [expanded]="false"
            (viewRegistration)="openFunctionRegistrationData($event)">
          </app-task-submission-panel>
        </mat-accordion>
      }
    }

    @if (data.registration.state.name !== 'FINISHED' && data.registration.task.toSubmit) {
      <div class="footer-row">
        <button mat-raised-button color="primary" (click)="resubmit = true">
          {{ 'case.tasks.resubmit' | translate | capitalize }}
        </button>
      </div>
    }
  }

  <!-- Workgroup members (only for workgroup registrations) -->
  @if (!data.registration.workgroup.isStudent) {
    <div class="content-card workgroup-card">
      <p class="section-header">{{ data.registration.workgroup.name }}</p>
      @for (student of data.registration.workgroup.students | sortBy: 'asc' : 'lastname'; track student) {
        <span class="workgroup-member">{{ student.firstname }} {{ student.lastname }}</span>
      }
    </div>
  }

  <!-- Submit button -->
  @if (
    (!data.registration.registrations || data.registration.registrations.length < 1 || resubmit) &&
    data.registration.state &&
    data.registration?.state?.name !== 'FINISHED' &&
    !data?.registration?.task?.isQuiz &&
    data.registration.task.toSubmit
  ) {
    <div class="footer-row">
      <app-spinner-button [config]="btnConfig" (onClick)="submit()"></app-spinner-button>
    </div>
  }
</div>
